package com.hyperether.goodjob.scenes.jobs.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.material.Card
import androidx.compose.material.Text
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupProperties
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.repository.Mapper
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.dropdown_arrow
import com.hyperether.goodjob.resources.name
import com.hyperether.goodjob.scenes.jobs.JobDetailsViewModel
import com.hyperether.goodjob.util.JobStatusMap
import com.hyperether.goodjob.util.StatusColor
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp
import com.hyperether.resources.stringResource
import org.jetbrains.compose.resources.painterResource

@Composable
fun JobDetailsHeaderView(isMobileView: Boolean, job: Job?, viewModel: JobDetailsViewModel) {
    val jobStatusMapAdmin = JobStatusMap()
    val selectedOption = remember {
        mutableStateOf(
            viewModel.jobStatusMap.entries.find { entry -> entry.value == job?.status }?.key
                ?: jobStatusMapAdmin.entries.find { entry -> entry.value == job?.status }?.key ?: ""
        )
    }
    val boxColor = remember {
        mutableStateOf(
            StatusColor.getStatusColor(viewModel.jobStatusMap[selectedOption.value])
        )
    }
    LaunchedEffect(job?.status) {
        boxColor.value = StatusColor.getStatusColor(job?.status)
        selectedOption.value =
            viewModel.jobStatusMap.entries.find { entry -> entry.value == job?.status }?.key
                ?: jobStatusMapAdmin.entries.find { entry -> entry.value == job?.status }?.key ?: ""
    }
    if (isMobileView) {
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.toScaleDp(), end = 16.toScaleDp(), top = 16.toScaleDp()),
            shape = RoundedCornerShape(8.toScaleDp()),
            elevation = 8.toScaleDp()
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.toScaleDp())
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        fontSize = 18.toScaleSp(),
                        fontWeight = FontWeight.W700,
                        text = job?.jobTitle ?: stringResource(Res.string.name),
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Spacer(modifier = Modifier.height(16.toScaleDp()))
                    DisableSelection {
                        DropdownJobStatus(
                            isMobileView = isMobileView,
                            allStatusMap = jobStatusMapAdmin,
                            jobStatusMap = viewModel.jobStatusMap,
                            selectedValue = selectedOption,
                            onValueSelected = { selectedValue ->
                                job?.id?.let {
                                    viewModel.updateJob(
                                        it,
                                        Mapper().toJobUpdateStatusRequest(
                                            job,
                                            viewModel.jobStatusMap[selectedValue] ?: ""
                                        ),
                                        onSuccess = { selectedOption.value = selectedValue },
                                        onError = {}
                                    )
                                }
                            },
                            boxColor = boxColor,
                            modifier = Modifier,
                            isEmployee = viewModel.isEmployee()
                        )
                    }
                }
            }
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth()
        ) {
            Text(
                fontSize = 24.toScaleSp(),
                fontWeight = FontWeight.W700,
                text = job?.jobTitle ?: stringResource(Res.string.name),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f)
            )
            Spacer(modifier = Modifier.width(24.toScaleDp()))
            DisableSelection {
                DropdownJobStatus(
                    isMobileView = isMobileView,
                    allStatusMap = jobStatusMapAdmin,
                    jobStatusMap = viewModel.jobStatusMap,
                    selectedValue = selectedOption,
                    onValueSelected = { selectedValue ->
                        job?.id?.let {
                            viewModel.updateJob(
                                it,
                                Mapper().toJobUpdateStatusRequest(
                                    job,
                                    viewModel.jobStatusMap[selectedValue] ?: ""
                                ),
                                onSuccess = { selectedOption.value = selectedValue },
                                onError = {}
                            )
                        }
                    },
                    boxColor = boxColor,
                    modifier = Modifier,
                    isEmployee = viewModel.isEmployee()
                )
            }
        }
    }
}

@Composable
fun DropdownJobStatus(
    isMobileView: Boolean,
    allStatusMap: Map<String, String>,
    jobStatusMap: Map<String, String>,
    selectedValue: MutableState<String>,
    boxColor: MutableState<Color>,
    onValueSelected: (String) -> Unit,
    modifier: Modifier,
    isEmployee: Boolean
) {
    var expanded by remember { mutableStateOf(false) }
    var textFieldSize = remember { mutableStateOf(Size.Zero) }
    val density = LocalDensity.current

    val jobCompleted = allStatusMap[selectedValue.value] == JobStatus.completed.name
    val isPendingApproval = allStatusMap[selectedValue.value] == JobStatus.pendingApproval.name
    val isCancelled = allStatusMap[selectedValue.value] == JobStatus.cancelled.name
    val isOpen = allStatusMap[selectedValue.value] == JobStatus.open.name
    val isInProgress = allStatusMap[selectedValue.value] == JobStatus.in_progress.name

    // Manager can change all status other then completed
    // Employee can change only to in progress status
    val isDropdownEnabled = !isEmployee || (isOpen || isInProgress)

    val handleExpandedState = {
        if (isDropdownEnabled) {
            expanded = !expanded
        }
    }

    if (isMobileView) {
        Row(
            modifier = modifier
                .height(40.toScaleDp())
                .width(160.toScaleDp())
                .background(color = boxColor.value, shape = RoundedCornerShape(24))
                .onGloballyPositioned { coordinates ->
                    textFieldSize.value = coordinates.size.toSize()
                }
                .clickable(
                    enabled = isDropdownEnabled,
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) { handleExpandedState() }
                .padding(horizontal = 12.toScaleDp(), vertical = 5.toScaleDp()),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .weight(1f),
                contentAlignment = Alignment.CenterStart
            ) {
                Text(
                    text = selectedValue.value,
                    color = Color.White,
                    fontSize = 12.toScaleSp(),
                    textAlign = TextAlign.Start,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            if (isDropdownEnabled) {
                Icon(
                    painter = painterResource(Res.drawable.dropdown_arrow),
                    contentDescription = null,
                    tint = if (isMobileView) Color.White else Color.Gray,
                    modifier = Modifier
                        .padding(start = 8.toScaleDp())
                        .size(9.toScaleDp())
                )
                DropdownMenu(
                    expanded = expanded,
                    onDismissRequest = { expanded = false },
                    modifier = Modifier.width(with(LocalDensity.current) { textFieldSize.value.width.toDp() })
                ) {
                    jobStatusMap.keys.forEach { key ->
                        DropdownMenuItem(
                            modifier = Modifier.height(35.toScaleDp()),
                            onClick = {
                                selectedValue.value = key
                                expanded = false
                                onValueSelected(key)
                            },
                            text = {
                                Text(
                                    fontSize = 16.toScaleSp(),
                                    text = key,
                                    softWrap = true,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        )
                    }
                }
            }
        }
    } else {
        Box(
            modifier = Modifier.background(
                boxColor.value.copy(alpha = 0.1f),
                RoundedCornerShape(8.toScaleDp())
            )
                .width(220.toScaleDp())
                .height(35.toScaleDp())
                .padding(horizontal = 8.toScaleDp(), vertical = 4.toScaleDp())
                .pointerHoverIcon(PointerIcon.Hand),
            contentAlignment = Alignment.CenterStart
        ) {
            Row(
                modifier = Modifier.fillMaxWidth()
                    .onGloballyPositioned { coordinates ->
                        textFieldSize.value = coordinates.size.toSize()
                    }
                    .clickable(
                        enabled = isDropdownEnabled,
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null
                    ) { handleExpandedState() },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(16.toScaleDp())
                        .background(
                            color = boxColor.value,
                            shape = CircleShape
                        )
                )
                Spacer(modifier = Modifier.width(15.toScaleDp()))
                Text(
                    text = selectedValue.value,
                    fontSize = 16.toScaleSp(),
                    color = Color.Black,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (isDropdownEnabled) {
                    Spacer(modifier = Modifier.width(15.toScaleDp()))
                    Icon(
                        painter = painterResource(Res.drawable.dropdown_arrow),
                        contentDescription = null,
                        tint = if (isMobileView) Color.White else Color.Gray,
                        modifier = Modifier
                            .padding(start = 8.toScaleDp())
                            .size(9.toScaleDp())
                    )
                }
            }

            if (isDropdownEnabled && expanded) {
                Popup(
                    alignment = Alignment.TopStart,
                    offset = IntOffset(
                        0,
                        with(LocalDensity.current) { 35.toScaleDp().toPx().toInt() }),
                    onDismissRequest = { expanded = false },
                    properties = PopupProperties(
                        clippingEnabled = false,
                        focusable = false
                    )
                ) {
                    Card(
                        backgroundColor = Color.White,
                        modifier = Modifier
                            .width(with(LocalDensity.current) { textFieldSize.value.width.toDp() }),
                        elevation = 4.dp,
                        shape = RoundedCornerShape(4.dp),
                    ) {
                        Column(
                            modifier.background(boxColor.value.copy(alpha = 0.1f))
                        ) {
                            jobStatusMap.keys.forEach { key ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(35.toScaleDp())
                                        .clickable {
                                            selectedValue.value = key
                                            expanded = false
                                            onValueSelected(key)
                                        }
                                        .padding(horizontal = 16.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    Text(
                                        fontSize = 16.toScaleSp(),
                                        text = key,
                                        softWrap = true,
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}